import { ADDRESS_FIAS_LEVEL_STREET } from '@sravni/cosago-react-library/lib/validationSchemes';

import { BFF_PROXY_API } from 'constants/apiRoutes';
import { REGEXPS } from 'constants/regexps';

import { axiosWithoutRetries } from 'shared/api/requestInstance';
import type { TDadataFullAddressSelectOption } from 'shared/types/TDaData';

/** Отделяет от адреса второстепенные части, которые мешают распознаванию */
export const splitAddressToSuggest = (value?: string) => {
  const [main, second = ''] = (value || '').split(
    /,\s?((((зон(а)?|эт(аж)?|пом(ещ)?(ение)?|в\/ч|воинская|офис|контора)\s).*)|(промзона)$)/gi,
  );

  return [
    main.replace(/корпус/gi, 'к').replace(REGEXPS.AddressPostalCode, ''),
    second.toLowerCase().replace(/\s[а-яё](?=[^а-яё])/gi, (a) => a.toUpperCase()),
  ] as const;
};

const ADDRESS_STREET_HOUSE_FIAS_LEVEL = [String(ADDRESS_FIAS_LEVEL_STREET)];

const isAddressDataHasHouse = (data: TDadataFullAddressSelectOption['data']) => {
  const { house, street, street_with_type, city, city_with_type, settlement, settlement_with_type, fias_level } = data;

  const place = city || settlement || city_with_type || settlement_with_type;
  const streetValue = street || street_with_type;

  return (
    Boolean(streetValue || place) &&
    Boolean(fias_level) &&
    (Boolean(house) || ADDRESS_STREET_HOUSE_FIAS_LEVEL.includes(String(fias_level)))
  );
};

const isMilitaryAddress = (addressPart: string, data: TDadataFullAddressSelectOption['data']) => {
  const { fias_level } = data;
  return addressPart && Number(fias_level) >= 4 && /((в\/ч|воинская)\s.+$)/gi.test(addressPart);
};

const isAddressDataHasTerritory = (data: TDadataFullAddressSelectOption['data']) => {
  const { fias_level, street_type, settlement_type } = data;
  const exceptions = ['тер', 'мкр', 'стр', 'г-к', 'пл-ка'];

  return (
    Number(fias_level) >= 6 && (exceptions.includes(street_type || '') || exceptions.includes(settlement_type || ''))
  );
};

const getStreetAndHouseFromAddress = (value: string) => {
  const parts = String(value || '')
    .split(',')
    .map((p) => p.trim())
    .filter(Boolean);

  // обычно хватает "город, улица" чтобы корректно сгруппировать подсказки
  const street = parts.slice(0, 2).join(', ');

  return { street };
};

const alternativeCheck = (_address: string, items: TDadataFullAddressSelectOption[]) => {
  if (!items.length) {
    return false;
  }

  const { data, value } = items[0];
  const { street: streetPart } = getStreetAndHouseFromAddress(value);

  if (items.every((item) => item.value.startsWith(streetPart))) {
    return isAddressDataHasHouse(data) || isAddressDataHasTerritory(data);
  }

  return false;
};

const getFitAddress = async (query: string): Promise<TDadataFullAddressSelectOption | null> => {
  try {
    const { data } = await axiosWithoutRetries.post<TDadataFullAddressSelectOption[]>(
      BFF_PROXY_API.getFullAddressesAutocomplete,
      {
        search: query,
        bound: 'house',
      },
    );

    return Array.isArray(data) ? (data[0] ?? null) : null;
  } catch {
    return null;
  }
};

export const checkWorkAddress = async (address: string) => {
  const [mainPart, secondPart] = splitAddressToSuggest(address);
  const fit = mainPart ? await getFitAddress(mainPart) : null;

  if (!fit) {
    return false;
  }

  return Boolean(
    isAddressDataHasHouse(fit.data) ||
      alternativeCheck(address, [{ ...fit, value: mainPart }]) ||
      isMilitaryAddress(secondPart, fit.data),
  );
};
